import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { AgentFormValues } from './agentShared';
import { ToolPicker } from './ScreensTab';
import { Chip } from '@/components/UI/Chip';
import { fieldClass } from '@/components/UI/fieldStyles';

export interface ConditionOption {
  label: string;
  expr: string;
}

interface Props {
  index: number;
  /** Ready-made conditions for the card / panel this block sits under; they fill the field in one click. */
  options: ConditionOption[];
  toolNames: string[];
  /** The condition in words ("Billing panel is open"). */
  describe?: string;
  globalNames: Set<string>;
  /** Attach tools picked here to the agent (same rule as the other tabs). */
  onAttach: (names: string[]) => void;
}

/** "Applies when" + "Brings tools" for one prompt block (backend: cards/conditions.py). */
const BlockCondition: React.FC<Props> = ({ index, options, toolNames, globalNames, onAttach, describe }) => {
  const { register, watch, setValue } = useFormContext<AgentFormValues>();
  const when = (watch(`system_prompts.${index}.applies_when`) as string | undefined) ?? '';
  const tools = (watch(`system_prompts.${index}.tools`) as string[] | undefined) ?? [];
  // The condition as the ready-made pieces joined by " or " (empty when it was typed by hand: then a chip replaces it).
  const known = new Set(options.map((o) => o.expr));
  const split = when.trim() ? when.split(' or ').map((x) => x.trim()) : [];
  const parts = split.every((x) => known.has(x)) ? split : [];
  /** Clicking a chip adds it with "or", clicking it again takes it out; a hand-written condition is replaced by the chip. */
  const toggleOption = (expr: string): string => (
    parts.includes(expr) ? parts.filter((x) => x !== expr).join(' or ') : [...parts, expr].join(' or ')
  );
  const [showOptions, setShowOptions] = useState(false);  // the ready-made conditions start folded

  return (
    <div className="space-y-2 border-b border-ui-border bg-ui-subtle px-3 py-2">
      <div>
        <label className="text-xs font-medium text-ui-text2" htmlFor={`when-${index}`}>Applies when</label>
        <input
          id={`when-${index}`}
          {...register(`system_prompts.${index}.applies_when`)}
          placeholder="screen.open('billing')   — empty: use the card or panel this block sits under"
          className={fieldClass({ size: 'xs', shape: 'box' }) + ' mt-1 w-full font-mono'}
        />
        {describe && <p className="mt-1 text-xs text-ui-text2">Means: {describe}</p>}
        {!when.trim() && <p className="mt-1 text-xs text-warning-text">No condition yet: this block applies on every screen until you set one.</p>}
        <button
          type="button"
          onClick={() => setShowOptions((v) => !v)}
          aria-expanded={showOptions}
          className="mt-1.5 inline-flex items-center gap-1.5 rounded text-xs font-medium text-ui-text3 transition hover:text-ui-text2 focus-ring"
        >
          <span className={`inline-block transition-transform ${showOptions ? 'rotate-90' : ''}`}>▸</span>
          Ready-made conditions ({options.length})
        </button>
        {when && (
          <button
            type="button"
            onClick={() => setValue(`system_prompts.${index}.applies_when`, '', { shouldDirty: true })}
            className="ml-3 rounded text-xs font-medium text-ui-text3 transition hover:text-ui-text2 focus-ring"
          >
            Clear
          </button>
        )}
        {showOptions && (
          <>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {options.map((o) => (
                <Chip key={o.expr} selected={parts.includes(o.expr)} title={o.expr}
                  onClick={() => setValue(`system_prompts.${index}.applies_when`, toggleOption(o.expr), { shouldDirty: true })}>
                  {o.label}
                </Chip>
              ))}
            </div>
            <p className="mt-1 text-xs text-ui-text3">
              Click several to combine them with “or”, or type and / or / not, e.g. <span className="font-mono">screen.card == 'engagement.view' and not screen.open('billing')</span>.
            </p>
          </>
        )}
      </div>
      <div>
        <span className="text-xs font-medium text-ui-text2">Brings tools <span className="text-ui-text3">({tools.length})</span></span>
        <ToolPicker
          names={toolNames}
          selected={tools}
          globalNames={globalNames}
          onChange={(pending) => { onAttach(pending); setValue(`system_prompts.${index}.tools`, pending, { shouldDirty: true }); }}
          label="+ add tools to this block"
          emptyText="No tools: the block only adds text."
        />
        <p className="mt-0.5 text-xs text-ui-text3">Offered to SAM exactly while the condition holds.</p>
      </div>
    </div>
  );
};

export default BlockCondition;
